import React, { Component, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { compose } from '../../utils/compose';

import { withFirebase } from '../Firebase';
import * as ROUTES from '../../constants/routes';
import { getDashboardRouteForEmail } from '../../utils/dashboardRouting';
import { buildFullName, getNameFromEmail, isNameDerivedFromEmail } from '../../utils/userName';

import '../Auth/styles.css';

const SignInPage = () => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-hero">
        {/* Decorative background elements */}
        <div className="hero-motif">
          <div className="hero-motif-gradient-1" />
          <div className="hero-motif-gradient-2" />
          <div className="hero-motif-gradient-3" />
          <span className="hero-motif-line" />
          <div className="hero-motif-orb" />
        </div>
        
        <div className="auth-hero-content">
          <span className="auth-overline">Douglas &amp; McCartney</span>
          <h1 className="auth-title headline-reveal">Welcome back.</h1>
          <p className="auth-subtitle">Access your Douglas &amp; McCartney workspace.</p>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-card">
          <header className="auth-form-header">
            <span className="auth-brand-wordmark">Douglas &amp; McCartney</span>
            <h2>Sign in</h2>
            <p>Enter your credentials or continue with a linked provider.</p>
          </header>

          <div className="auth-oauth-section">
            <SignInGoogle />
            <div className="auth-separator">
              <span>or</span>
            </div>
          </div>

          <SignInForm />
        </div>
      </div>
    </div>
  );
};

const INITIAL_STATE = {
  email: '',
  password: '',
  remember: false,
  error: null,
};

const ERROR_CODE_ACCOUNT_EXISTS =
  'auth/account-exists-with-different-credential';

const ERROR_MSG_ACCOUNT_EXISTS = `
  An account with an E-Mail address to
  this social account already exists. Try to login from
  this account instead and associate your social accounts on
  your personal account page.
`;

const SIGN_IN_REDIRECT_QUERY_KEY = 'redirect';

const getRequestedRedirect = (location) => {
  const search = typeof location?.search === 'string' ? location.search : '';
  if (!search) {
    return '';
  }

  const redirectPath = new URLSearchParams(search).get(SIGN_IN_REDIRECT_QUERY_KEY)?.trim() || '';
  if (!redirectPath || !redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    return '';
  }

  return redirectPath;
};

const getContextualRedirect = (location) => {
  const pathname = typeof location?.pathname === 'string' ? location.pathname : '';

  if (pathname === ROUTES.FRANCHISE_REGISTRATION_DASHBOARD) {
    return `${ROUTES.DASHBOARD}?view=franchise`;
  }

  return '';
};

const getPostSignInRoute = (location, email) =>
  getRequestedRedirect(location) || getContextualRedirect(location) || getDashboardRouteForEmail(email);

class SignInFormBase extends Component {
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  onSubmit = event => {
    const { email, password } = this.state;

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('lastLoginAt');
    }

    this.props.firebase
      .doSignInWithEmailAndPassword(email, password)
      .then(() => {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('lastLoginAt', String(Date.now()));
        }
        this.setState({ ...INITIAL_STATE });
        this.props.history.push(getPostSignInRoute(this.props.location, email));
      })
      .catch(error => {
        this.setState({ error });
      });

    event.preventDefault();
  };

  onChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  onToggleRemember = event => {
    this.setState({ remember: event.target.checked });
  };

  render() {
    const { email, password, remember, error } = this.state;

    const isInvalid = password === '' || email === '';

    return (
      <form onSubmit={this.onSubmit} className="auth-form" noValidate>
        <div className="auth-field">
          <label htmlFor="signin-email">Email</label>
          <input
            id="signin-email"
            name="email"
            value={email}
            onChange={this.onChange}
            type="email"
            placeholder="name@example.com"
            required
          />
        </div>

        <div className="auth-field">
          <label htmlFor="signin-password">Password</label>
          <input
            id="signin-password"
            name="password"
            value={password}
            onChange={this.onChange}
            type="password"
            placeholder="Enter your password"
            required
          />
        </div>

        <div className="auth-remember-row">
          <label htmlFor="signin-remember" className="auth-remember">
            <input
              id="signin-remember"
              name="remember"
              type="checkbox"
              checked={remember}
              onChange={this.onToggleRemember}
              className="checkbox-premium"
            />
            <span>Remember</span>
          </label>
          <Link to={ROUTES.PASSWORD_FORGET} className="auth-link auth-link--sm">
            Forgot password?
          </Link>
        </div>

        {error && <p className="auth-error">{error.message}</p>}

        <button disabled={isInvalid} type="submit" className="auth-submit">
          Sign In
        </button>

        <div style={{ 
          marginTop: '1.5rem',
          padding: '1.5rem',
          background: 'rgba(168, 168, 168, 0.05)',
          borderRadius: '20px',
          border: '1px solid rgba(168, 168, 168, 0.15)',
          textAlign: 'center'
        }}>
          <p style={{ 
            margin: '0 0 1rem 0',
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#a8a8a8'
          }}>
            New to Douglas &amp; McCartney?
          </p>
          <Link
            to={ROUTES.SIGN_UP}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              fontWeight: '700',
              fontSize: '0.95rem',
              color: '#f8f8f8',
              background: '#0a0a0a',
              borderRadius: '999px',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(248, 248, 248, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.background = '#1a1a1a';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.background = '#0a0a0a';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
            }}
          >
            <span>Create an account</span>
            <svg style={{ height: '1.1rem', width: '1.1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

        <p className="auth-terms">
          By continuing, you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="auth-link auth-link--inline">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="auth-link auth-link--inline">
            Privacy Policy
          </a>
          .
        </p>
      </form>
    );
  }
}

class SignInGoogleBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null };
  }

  onSubmit = event => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('lastLoginAt');
    }

    this.props.firebase
      .doSignInWithGoogle()
      .then(socialAuthUser => {
        const nextRoute = getPostSignInRoute(this.props.location, socialAuthUser?.user?.email);
        const userId = socialAuthUser?.user?.uid;
        const email = socialAuthUser?.user?.email || '';
        const emailName = getNameFromEmail(email);
        const profile = socialAuthUser?.additionalUserInfo?.profile || {};
        const profileFullName =
          typeof profile?.name === 'string' && profile.name.trim()
            ? profile.name.trim()
            : [profile?.given_name, profile?.family_name]
                .filter((part) => typeof part === 'string' && part.trim())
                .join(' ')
                .trim();
        const providerDisplayName =
          profileFullName || (socialAuthUser?.user?.displayName || '').trim();

        if (!userId) {
          return nextRoute;
        }

        const userRef = this.props.firebase.user(userId);
        return userRef.get().then((snapshot) => {
          const existingUser = snapshot.exists ? snapshot.data() || {} : {};
          const existingManualName = buildFullName(existingUser.firstName, existingUser.lastName);
          const isManualAccount =
            String(existingUser.signupMethod || '').toLowerCase() === 'manual' ||
            Boolean(existingManualName);
          const payload = {
            email,
          };

          if (!isManualAccount) {
            const shouldSyncFullName =
              !existingUser.fullName ||
              isNameDerivedFromEmail(existingUser.fullName, email);
            const shouldSyncUsername =
              !existingUser.username ||
              isNameDerivedFromEmail(existingUser.username, email);

            if (shouldSyncFullName) {
              payload.fullName = providerDisplayName || emailName;
            }
            if (shouldSyncUsername) {
              payload.username = providerDisplayName || emailName;
            }
            if (!existingUser.signupMethod) {
              payload.signupMethod = 'google';
            }
          }

          return userRef.set(payload, { merge: true }).then(() => nextRoute);
        });
      })
      .then((nextRoute) => {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('lastLoginAt', String(Date.now()));
        }
        this.setState({ error: null });
        this.props.history.push(nextRoute || ROUTES.DASHBOARD);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = ERROR_MSG_ACCOUNT_EXISTS;
        }

        this.setState({ error });
      });

    event.preventDefault();
  };

  render() {
    const { error } = this.state;

    return (
      <form onSubmit={this.onSubmit} className="auth-oauth-form">
        <button type="submit" className="auth-oauth-button">
          <span className="auth-oauth-icon">
            <svg height="18" viewBox="0 0 18 18" width="18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64,9.20454545 C17.64,8.56636364 17.5827273,7.95272727 17.4763636,7.36363636 L9,7.36363636 L9,10.845 L13.8436364,10.845 C13.635,11.97 13.0009091,12.9231818 12.0477273,13.5613636 L12.0477273,15.8195455 L14.9563636,15.8195455 C16.6581818,14.2527273 17.64,11.9454545 17.64,9.20454545 L17.64,9.20454545 Z" fill="#4285F4"/>
              <path d="M9,18 C11.43,18 13.4672727,17.1940909 14.9563636,15.8195455 L12.0477273,13.5613636 C11.2418182,14.1013636 10.2109091,14.4204545 9,14.4204545 C6.65590909,14.4204545 4.67181818,12.8372727 3.96409091,10.71 L0.957272727,10.71 L0.957272727,13.0418182 C2.43818182,15.9831818 5.48181818,18 9,18 L9,18 Z" fill="#34A853"/>
              <path d="M3.96409091,10.71 C3.78409091,10.17 3.68181818,9.59318182 3.68181818,9 C3.68181818,8.40681818 3.78409091,7.83 3.96409091,7.29 L3.96409091,4.95818182 L0.957272727,4.95818182 C0.347727273,6.17318182 0,7.54772727 0,9 C0,10.4522727 0.347727273,11.8268182 0.957272727,13.0418182 L3.96409091,10.71 L3.96409091,10.71 Z" fill="#FBBC05"/>
              <path d="M9,3.57954545 C10.3213636,3.57954545 11.5077273,4.03363636 12.4404545,4.92545455 L15.0218182,2.34409091 C13.4631818,0.891818182 11.4259091,0 9,0 C5.48181818,0 2.43818182,2.01681818 0.957272727,4.95818182 L3.96409091,7.29 C4.67181818,5.16272727 6.65590909,3.57954545 9,3.57954545 L9,3.57954545 Z" fill="#EA4335"/>
            </svg>
          </span>
          <span>Continue with Google</span>
        </button>

        {error && <p className="auth-error">{error.message}</p>}
      </form>
    );
  }
}

const SignInForm = compose(
  withRouter,
  withFirebase,
)(SignInFormBase);

const SignInGoogle = compose(
  withRouter,
  withFirebase,
)(SignInGoogleBase);

export default SignInPage;

export { SignInForm, SignInGoogle };
